<template>
    <!-- Chart Content Wrapper -->
    <div v-if="allDisplayData.length > 0" ref="chartContentWrapperRef" class="chart-content-wrapper">
        <!-- Chart -->
        <div class="chart-wrapper" :key="`${selectedTab}${canvasWidth}${getAppThemeMode}`">
            <chart-complex
            v-if="!hideChart"
            identifier="dashboard"
            :graphData="graphData"
            :colors="graphData.length && graphData[0][0] === 'empty' ? [notSelectedColor] : colorsForGraph"
            :hideLabels="isMobile || (graphData.length && graphData[0][0] === 'empty')"
            :hoverIndex="chartIsRendered ? centerPercentageParams.index : -1"
            :customSize="anchorWidgetChartSize"
            :canvasWidth="canvasWidth"
            :centerPercentageParams="centerPercentageParams"
            :showZeroTooltip="isMobile ? centerPercentageParams.percentage === 0 : false"
            :isMobile="isMobile"
            @mouseHover="handleGraphHover($event)"
            @mouseOut="hasLabelOver = false"
            @chartIsRendered="chartIsRendered = $event">
                <!-- Tooltip component to display element hovered info -->
                <tooltip
                v-if="!isMobile && tooltipInfo"
                :key="labelHoverIndex"
                :colors="colors"
                :labelHoverIndex="labelHoverIndex"
                :tooltipInfo="tooltipInfo"
                :selectedProduct="selectedProduct"/>
            </chart-complex>
        </div>
        <!-- Product cards -->
        <product-cards-container
        v-if="isMobile && graphData[0][0] !== 'empty'"
        :key="index"
        :products="allDisplayData"
        :colors="colors"
        :selectedTab="selectedTab"
        @scrolledToCard="setCenterPercentageParams" />
    </div>
</template>

<script setup>
import ChartComplex from '@/components/generic/charts/Complex'
import Tooltip from '@/components/features/home/widgets/patrimonyTabChart/desktop/Tooltip'
import ProductCardsContainer from '@/components/features/home/widgets/patrimonyTabChart/mobile/ProductCardsContainer'
/* Vue & Assets */
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import {
    calculateActivePassivePercentage, createActivePassiveGraphArray, calculateProductTotalAmount, calculateProductPercentage, createProductGraphArray
} from '@/assets/scripts/parsers/patrimony'
/* Store */
import { mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['showEmptyState'])

/** props received
@selectedTab {String, Number}
@isMobile {Boolean}
@variant {String} - can be [ none (default), anchor]
*/
const props = defineProps({
    selectedTab: [String, Number],
    isMobile: Boolean,
    variant: String
})

/* mapGetters */
const { getAccountsProductsList, getPatrimonyAccountsList } = mapGetters(moduleMapper.HOME)
const { getAppThemeMode } = mapGetters()

/**********************************************************************
*                                                                     *
*                             Charts Logic                            *
*                                                                     *
**********************************************************************/
const hasLabelOver = ref(false)
const labelHoverIndex = ref(null)
const notSelectedColor = ref('#ebf1f3')
const centerPercentageParams = ref({ index: -1 })
const assetsVsLiabilitiesColors = ref(['var(--cashflow-entry)', 'var(--cashflow-exit)'])
const chartIsRendered = ref(false)
const hideChart = ref(true)
const chartContentWrapperRef = ref('')
const canvasWidth = ref(null)

const anchorWidgetChartSize = computed(() => {
    if (props.isMobile) return 260

    return props.variant === 'anchor' ? 220 : null
})

const allDisplayData = computed(() => {
    if (props.selectedTab === 'assetsVsLiabilities') {
        return getAccountFamilyGraph('labels', null)
    } else {
        return getProductGraph('labels', null, props.selectedTab)
    }
})

const graphData = computed(() => {
    if (props.selectedTab === 'assetsVsLiabilities') {
        return getAccountFamilyGraph('chart', null)
    } else {
        return getProductGraph('chart', null, props.selectedTab)
    }
})

/*
* We need to define our colors array based on:
*	- accountFinancialType being 'assetsVsLiabilities', it only has positive or negative color
*	- allProducts option, we create an array with only the colors of products available for this filter
*	- specific product, we create an array with the product color as base
*/
const colors = computed(() => {
    if (props.selectedTab === 'assetsVsLiabilities') return assetsVsLiabilitiesColors.value

    return createProductsPalette()
})

/*
* We need to have a diferent array for graph, since we only show positive values
*/
const colorsForGraph = computed(() => {
    if (props.selectedTab === 'assetsVsLiabilities') return assetsVsLiabilitiesColors.value

    return createProductsPalette(true)
})

/*
* Getting info from our allDisplayData that matches our hovered element
*/
const tooltipInfo = computed(() => {
    return allDisplayData.value[labelHoverIndex.value]
})

/*
* Graphic to compare assets and liabilities
*/
const getAccountFamilyGraph = (displayType, selectedCurrency) => {
    let totalAmount = 0
    let assetsTotal = 0
    let liabilitiesTotal = 0

    getPatrimonyAccountsList.value(selectedCurrency, null, 'assets').forEach(account => {
        assetsTotal += account.convertedBalance
        totalAmount += account.convertedBalance
    })

    getPatrimonyAccountsList.value(selectedCurrency, null, 'liabilities').forEach(account => {
        liabilitiesTotal += Math.abs(account.convertedBalance)
        totalAmount += Math.abs(account.convertedBalance)
    })

    // We have to create this empty array, to be display "empty" chart when we have values, but their total % equals 0
    if (totalAmount === 0 && assetsTotal === 0 && liabilitiesTotal === 0) return [['empty', 100]]

    const graphArray = [
        {
            id: 0,
            description: t('home.widgets.patrimony.labels.assets'),
            totalAmount: assetsTotal,
            accounts: getPatrimonyAccountsList.value(null, null, 'assets')
        },
        {
            id: 1,
            description: t('home.widgets.patrimony.labels.liabilities'),
            totalAmount: liabilitiesTotal,
            accounts: getPatrimonyAccountsList.value(null, null, 'liabilities')
        }
    ]

    return displayType === 'chart'
        ? createActivePassiveGraphArray(calculateActivePassivePercentage(graphArray, totalAmount))
        : calculateActivePassivePercentage(graphArray, totalAmount)
}

/*
* Graphic with all Products based in selectedCurrency and accountFinancialType
*/
const getProductGraph = (displayType, selectedCurrency, accountFinancialType) => {
    const graphArray = []
    let totalAmount = 0

    getAccountsProductsList.value(null, getAppThemeMode.value).forEach(product => {
        if (getPatrimonyAccountsList.value(selectedCurrency, product.id, accountFinancialType).length) {
            const productInfo = {
                id: product.id,
                color: product.color,
                description: product.description,
                accounts: getPatrimonyAccountsList.value(selectedCurrency, product.id, accountFinancialType)
            }

            productInfo.totalAmount = calculateProductTotalAmount(productInfo.accounts)
            totalAmount += productInfo.totalAmount
            graphArray.push(productInfo)
        }
    })

    // We have to create this empty array, to be display "empty" chart when we have values, but their total % equals 0
    if (totalAmount === 0 && displayType === 'chart') return [['empty', 100]]

    return displayType === 'chart' ? createProductGraphArray(calculateProductPercentage(graphArray, totalAmount)) : calculateProductPercentage(graphArray, totalAmount)
}

/**
* Creates an array with only the colors of products available for this filter
* We use allDisplayData, cuz it has all the info including products with 0%
@onlyPositiveValues {Boolean} - used to filter colors of products with 0%, used for chart since it only receives positive products
*/
const createProductsPalette = onlyPositiveValues => {
    if (onlyPositiveValues) return allDisplayData.value.filter(obj => obj.percentage !== 0).map(obj => { return obj.color })

    return allDisplayData.value.map(obj => { return obj.color })
}

/**********************************************************************
*                                                                     *
*                     Handle for Chart Hovers&Clicks                  *
*                                                                     *
**********************************************************************/
/*
* Validation which label of all data is the right one
*/
const handleGraphHover = ($event) => {
    /* No point in hovering chart to display tooltip, we don't even know which of the accounts to show, cuz there is no slice for it */
    if ($event.data.label === 'EMPTY') return

    hasLabelOver.value = true

    labelHoverIndex.value = allDisplayData.value.findIndex(obj => {
        return obj.description?.toLowerCase() === $event.data.sliceId?.toLowerCase()
    })
}

const setCenterPercentageParams = event => {
    let tempIndex = -1

    if (event.percentage > 0) {
        tempIndex = graphData.value.findIndex(data => data[2] === event.id)
    }

    centerPercentageParams.value = {
        ...event,
        index: tempIndex
    }
}

const checkWidgetSize = () => {
    hideChart.value = true
    setTimeout(() => {
        canvasWidth.value = chartContentWrapperRef.value.clientWidth
        hideChart.value = false
    })
}

onMounted(() => {
    if (allDisplayData.value.length === 0 || allDisplayData.value[0][0] === 'empty') emit('showEmptyState')
    canvasWidth.value = chartContentWrapperRef.value.clientWidth
    hideChart.value = false
    window.addEventListener('resize', checkWidgetSize)
})

onBeforeUnmount(() => {
	window.removeEventListener('resize', checkWidgetSize)
})
</script>

<style lang="scss" scoped>
.generic-tabs-wrapper {
    width: auto;
    margin: 12px 0 16px 0;
}

.patrimony-empty-state {
    @include commonEnteringAnimation;
}

.chart-content-wrapper {
    position: relative;

    width: 100%;

    padding-top: 15px;

    .chart-wrapper {
        width: 100%;

        .complex-chart-wrapper {
            display: flex;
            justify-content: center;
            align-items: center;

            width: 100%;
        }
    }
}
</style>
